import Header from "@/components/header";
import useIsSmall from "@/hooks/useIsSmall";
import { SmartAudienceKind, SmartAudienceListItemDto } from "@/models/club";
import IncreaseWallet from "@/modules/home/increaseWallet";
import { useCampaignWallet } from "@/modules/marketing/CampaignWallet";
import { audienceAction, audiencePath, audienceTitle, lastSentNote } from "@/modules/smartAudience/text";
import useSmsCreditAccess from "@/modules/smsCredit/useSmsCreditAccess";
import { useSmartAudiences } from "@/services/smartAudience/hooks";
import { Modal, Skeleton } from "@mantine/core";
import {
  IconChevronLeft,
  IconDiscount,
  IconHistory,
  IconMessage,
  IconSparkles,
  IconUsers,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import classes from "./sendMessage.module.scss";

// Three ways to send, named by what the salon wants to achieve rather than by the kind of SMS.
const goals = [
  {
    key: "customers",
    to: "/dashboard/messages/selectUser",
    icon: IconUsers,
    tone: "violet",
    tag: "مشتریان فعلی",
    title: "پیام به مشتری‌هایتان",
    text: "خبر، یادآوری یا تبریک؛ به همه، یک گروه یا چند نفر.",
  },
  {
    key: "discount",
    to: "/dashboard/discount/selectGroup",
    icon: IconDiscount,
    tone: "orange",
    tag: "برگرداندن و فروش بیشتر",
    title: "پیشنهاد تخفیف",
    text: "کد تخفیف اختصاصی برای هر نفر، با مبلغ یا درصد و تاریخ انقضا.",
  },
] as const;

const TODAY = 3;

const audienceNote = (audience: SmartAudienceListItemDto) =>
  lastSentNote(audience.lastSentAt) ??
  (audience.kind === SmartAudienceKind.Value1 ? "نوبت بعدی ندارند؛ متن آماده است." : "متن آماده است؛ هزینه را قبل از ارسال می‌بینید.");

function MessageDashboard() {
  const isSmall = useIsSmall();
  const navigate = useNavigate();
  const wallet = useCampaignWallet();
  const { allowed: canRecharge } = useSmsCreditAccess();
  const [recharge, setRecharge] = useState(false);
  const audiences = useSmartAudiences();
  const [showAll, setShowAll] = useState(false);
  const suggestions = audiences.data ?? [];
  const shown = showAll ? suggestions : suggestions.slice(0, isSmall ? 2 : TODAY);
  const hiddenCount = suggestions.length - shown.length;

  const balance = wallet.isLoading ? (
    <Skeleton width={110} height={18} />
  ) : (
    <strong>{wallet.data?.amount !== undefined ? `${wallet.data.amount.toLocaleString("fa-IR")} ریال` : "دریافت نشد"}</strong>
  );

  return (
    <div className={classes.page}>
      {isSmall && <Header showBack route="/dashboard/marketing" title="ارسال پیامک" />}
      <div className={classes.content}>
        <header className={classes.head}>
          <div className={classes.headText}>
            <h1>ارسال پیامک</h1>
            <p>هدفتان را انتخاب کنید؛ مخاطب، متن و هزینه را قدم‌به‌قدم می‌بینید.</p>
          </div>
          <Link to="/dashboard/messages" className={classes.history}>
            <IconHistory size={18} stroke={1.8} aria-hidden />
            تاریخچه
          </Link>
          <div className={classes.credit}>
            <span className={classes.creditIcon} aria-hidden>
              <IconMessage size={20} stroke={1.8} />
            </span>
            <span className={classes.creditText}>
              <span>اعتبار پیامک</span>
              {balance}
            </span>
            {canRecharge && (
              <button
                type="button"
                className={classes.creditAction}
                onClick={() => (isSmall ? navigate("/dashboard/sms-credit") : setRecharge(true))}
              >
                + افزایش
              </button>
            )}
          </div>
        </header>

        {!audiences.isError && (audiences.isLoading || suggestions.length > 0) && (
          <section className={classes.section} aria-labelledby="send-today">
            <div className={classes.sectionHead}>
              <h2 id="send-today">
                <IconSparkles size={18} stroke={1.8} aria-hidden />
                پیشنهاد امروز
              </h2>
              {(hiddenCount > 0 || showAll) && (
                <button type="button" className={classes.textButton} onClick={() => setShowAll((value) => !value)}>
                  {showAll ? "کمتر" : `همه (${suggestions.length.toLocaleString("fa-IR")})`}
                </button>
              )}
            </div>
            <div className={classes.today}>
              {audiences.isLoading
                ? [0, 1].map((row) => <Skeleton key={row} height={64} radius="lg" />)
                : shown.map((audience) => (
                    <Link
                      key={audience.key}
                      to={audiencePath(audience.key ?? "")}
                      className={classes.suggestion}
                      data-tone={audience.kind === SmartAudienceKind.Value1 ? "blue" : "violet"}
                    >
                      <span className={classes.suggestionText}>
                        <strong>{audienceTitle(audience)}</strong>
                        <span>{audienceNote(audience)}</span>
                      </span>
                      <span className={classes.suggestionAction}>
                        {audienceAction(audience.kind)}
                        <IconChevronLeft size={16} stroke={2} aria-hidden />
                      </span>
                    </Link>
                  ))}
            </div>
          </section>
        )}

        <section className={classes.section} aria-labelledby="send-new">
          <div className={classes.sectionHead}>
            <h2 id="send-new">پیامک جدید؛ هدفتان چیست؟</h2>
          </div>
          <div className={classes.goals}>
            {goals.map((goal) => {
              const Icon = goal.icon;
              return (
                <Link key={goal.key} to={goal.to} className={classes.goal} data-tone={goal.tone}>
                  <span className={classes.goalIcon} aria-hidden>
                    <Icon size={24} stroke={1.8} />
                  </span>
                  <span className={classes.goalText}>
                    <span className={classes.goalTag}>{goal.tag}</span>
                    <strong>{goal.title}</strong>
                    <span>{goal.text}</span>
                  </span>
                  <span className={classes.goalAction}>
                    <span>شروع</span>
                    <IconChevronLeft size={18} stroke={2} aria-hidden />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      </div>

      <Modal
        opened={recharge}
        onClose={() => {
          setRecharge(false);
          void wallet.refetch();
        }}
        title="افزایش اعتبار پیامکی"
        centered
        size={520}
        radius={20}
      >
        {recharge && <IncreaseWallet />}
      </Modal>
    </div>
  );
}

export default MessageDashboard;
